import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "listbox", "clearBtn"]
  static values = {
    categories: Array,
    createUrl: String,
    placeholder: { type: String, default: "No category" },
    createText: { type: String, default: 'Create "%{name}"' }
  }

  connect() {
    this.selectedId = this.hiddenTarget.value || ""
    this.selectedName = this.inputTarget.value || ""
    this.isOpen = false
    this.highlightedIndex = -1
    this.creating = false
    this.filteredItems = []

    this._handleClickOutside = (event) => {
      if (!this.element.contains(event.target)) {
        this.close()
      }
    }
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
  }

  // --- Filtering & Rendering ---

  filter() {
    const raw = this.inputTarget.value.trim()
    const query = raw.toLowerCase()
    this.updateClearBtn()

    if (!query) {
      this.filteredItems = this.categoriesValue
    } else {
      this.filteredItems = this.categoriesValue.filter(c =>
        c.name.toLowerCase().includes(query)
      )
    }

    this.renderList(raw)
    if (!this.isOpen) this.open()
  }

  renderList(query = "") {
    const listbox = this.listboxTarget
    listbox.innerHTML = ""
    this.highlightedIndex = -1

    // "No category" option
    const noneItem = document.createElement("li")
    noneItem.className = "combobox-option combobox-option-empty"
    noneItem.setAttribute("role", "option")
    noneItem.textContent = this.placeholderValue
    noneItem.dataset.id = ""
    noneItem.addEventListener("click", () => this.selectItem("", ""))
    listbox.appendChild(noneItem)

    // Matched categories
    for (const cat of this.filteredItems) {
      const li = document.createElement("li")
      li.className = "combobox-option"
      if (String(cat.id) === String(this.selectedId)) {
        li.classList.add("is-selected")
      }
      li.setAttribute("role", "option")
      li.textContent = cat.name
      li.dataset.id = cat.id
      li.addEventListener("click", () => this.selectItem(cat.id, cat.name))
      listbox.appendChild(li)
    }

    // "Create new" option — only when query doesn't exactly match an existing category
    const trimmedQuery = query.trim()
    if (trimmedQuery) {
      const exactMatch = this.categoriesValue.some(c =>
        c.name.toLowerCase() === trimmedQuery.toLowerCase()
      )
      if (!exactMatch) {
        const createLi = document.createElement("li")
        createLi.className = "combobox-create-option"
        createLi.setAttribute("role", "option")
        createLi.textContent = this.createTextValue.replace("%{name}", trimmedQuery)
        createLi.dataset.createName = trimmedQuery
        createLi.addEventListener("click", () => this.createCategory(trimmedQuery))
        listbox.appendChild(createLi)
      }
    }
  }

  // --- Open / Close ---

  open() {
    if (this.isOpen) return
    this.isOpen = true

    // If there's a selected category, select all text and show full list
    // so user can immediately type to replace/filter
    if (this.selectedId) {
      this.inputTarget.select()
      this.filteredItems = this.categoriesValue
      this.renderList("")
    } else {
      const raw = this.inputTarget.value.trim()
      const query = raw.toLowerCase()
      if (!query) {
        this.filteredItems = this.categoriesValue
      } else {
        this.filteredItems = this.categoriesValue.filter(c =>
          c.name.toLowerCase().includes(query)
        )
      }
      this.renderList(raw)
    }

    this.positionListbox()
    this.listboxTarget.classList.add("is-open")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  positionListbox() {
    const rect = this.inputTarget.getBoundingClientRect()
    const footer = document.querySelector(".editor-footer")
    const footerHeight = footer ? footer.offsetHeight : 0
    const spaceBelow = window.innerHeight - rect.bottom - footerHeight
    const listboxHeight = 208 // max-height(200) + padding(8)
    this.listboxTarget.classList.toggle("is-above", spaceBelow < listboxHeight)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.listboxTarget.classList.remove("is-open")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1

    // Blur logic: exact match → select; empty → clear; no match → revert
    const inputVal = this.inputTarget.value.trim()
    if (!inputVal) {
      this.selectItem("", "")
    } else {
      const match = this.categoriesValue.find(c =>
        c.name.toLowerCase() === inputVal.toLowerCase()
      )
      if (match) {
        this.selectItem(match.id, match.name)
      } else {
        // Revert to last selection
        this.inputTarget.value = this.selectedName
        this.updateClearBtn()
      }
    }
  }

  // --- Selection ---

  selectItem(id, name) {
    this.selectedId = String(id)
    this.selectedName = name
    this.hiddenTarget.value = id
    this.inputTarget.value = name
    this.updateClearBtn()
    this.close()
    // Dispatch events for autosave
    this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clear() {
    this.selectItem("", "")
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  // --- Create ---

  async createCategory(name) {
    if (this.creating) return false
    this.creating = true

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ name })
      })

      if (!response.ok) {
        this.creating = false
        return false
      }

      const data = await response.json()
      // Add to categories list
      const updated = [...this.categoriesValue, { id: data.id, name: data.name }]
      this.categoriesValue = updated
      this.selectItem(data.id, data.name)
      return true
    } catch {
      return false
    } finally {
      this.creating = false
    }
  }

  // --- Keyboard Navigation ---

  handleKeydown(event) {
    if (!this.isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      this.open()
      return
    }

    if (!this.isOpen) {
      if (event.key === "Enter") event.preventDefault()
      return
    }

    const options = this.listboxTarget.querySelectorAll("[role='option']")
    const count = options.length

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, count - 1)
        this.updateHighlight(options)
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.updateHighlight(options)
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && this.highlightedIndex < count) {
          options[this.highlightedIndex].click()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  updateHighlight(options) {
    options.forEach((opt, i) => {
      opt.classList.toggle("is-highlighted", i === this.highlightedIndex)
      if (i === this.highlightedIndex) {
        opt.scrollIntoView({ block: "nearest" })
      }
    })
  }

  // --- Clear Button ---

  updateClearBtn() {
    if (!this.hasClearBtnTarget) return
    const hasValue = this.inputTarget.value.trim() || this.selectedId
    this.clearBtnTarget.hidden = !hasValue
  }

  // --- Public API (for AI assistant) ---

  selectById(id) {
    const cat = this.categoriesValue.find(c => String(c.id) === String(id))
    if (cat) {
      this.selectItem(cat.id, cat.name)
    }
  }

  async selectByNameOrCreate(name) {
    const match = this.categoriesValue.find(c =>
      c.name.toLowerCase().trim() === name.toLowerCase().trim()
    )
    if (match) {
      this.selectItem(match.id, match.name)
      return true
    } else {
      return await this.createCategory(name)
    }
  }

  addAndSelect(id, name) {
    // Add to categories list if not already present
    if (!this.categoriesValue.some(c => String(c.id) === String(id))) {
      this.categoriesValue = [...this.categoriesValue, { id, name }]
    }
    this.selectItem(id, name)
  }
}
